import { numberFormat } from '@/src/shared/utils'
import { appColors } from '@/src/theme'
import type { DataCompletenessScope } from './dataCompleteness.mapper'

export type DataCompletenessType = 'complete' | 'partial' | 'missing'

export const getNoMetersForScopeTranslationKey = (scope: DataCompletenessScope): string =>
  `components.data_completeness_indicator.no_meters.${scope}`

type DataQualityType = 'excellent' | 'good' | 'poor' | 'none'

type DataQualityColorStyle = {
  textColor: string
  background: string
  borderColor: string
}

export interface IDataCompletenessDataPoint {
  startTime: string
  endTime: string
  completenessPercentage: number
}

export interface IDataCompletenessItem {
  title: string
  id: number
  sourceId?: string
  sourceType?: string
  isPinned?: boolean
  completenessPercentage: number | null | undefined
  data: IDataCompletenessDataPoint[] | null | undefined
}

export const completenessIndicatorLayout = {
  containerWidthPx: 396,
  containerHorizontalPaddingPx: 12,
  containerVerticalPaddingPx: 8,
  dotSizePx: 6,
  dotMinGapPx: 1,
  dotRowGapPx: 2,
  itemsScrollPaddingRightPx: 4,
  itemRowGapPx: 4,
} as const

export const itemsScrollContainerStyle = {
  overflowY: 'auto',
  minHeight: 0,
  flex: '1 1 auto',
  pr: `${completenessIndicatorLayout.itemsScrollPaddingRightPx}px`,
  mr: `-${completenessIndicatorLayout.itemsScrollPaddingRightPx}px`,
} as const

export const VIRTUALIZATION_THRESHOLD = 50

const dotsRowWidthPx =
  completenessIndicatorLayout.containerWidthPx -
  2 * completenessIndicatorLayout.containerHorizontalPaddingPx -
  completenessIndicatorLayout.itemsScrollPaddingRightPx

const maxDotsPerRow = Math.floor(
  (dotsRowWidthPx + completenessIndicatorLayout.dotMinGapPx) /
    (completenessIndicatorLayout.dotSizePx + completenessIndicatorLayout.dotMinGapPx)
)

export const colorMap: Record<DataCompletenessType, string> = {
  complete: appColors.green['300'],
  partial: appColors.amber['500'],
  missing: appColors.red['600'],
}

export const qualityColorMap: Record<DataQualityType, DataQualityColorStyle> = {
  excellent: {
    textColor: appColors.positive,
    background: appColors.green['100'],
    borderColor: appColors.green['200'],
  },
  good: {
    textColor: appColors.warning,
    background: appColors.amber['50'],
    borderColor: appColors.amber['200'],
  },
  poor: {
    textColor: appColors.negative,
    background: appColors.red['100'],
    borderColor: appColors.red['200'],
  },
  none: {
    textColor: appColors.secondaryForeground,
    background: appColors.muted,
    borderColor: appColors.border,
  },
}

export const getDotsColumnGap = (dotCount: number): number => {
  if (dotCount <= 1) return completenessIndicatorLayout.dotMinGapPx
  if (dotCount > maxDotsPerRow) return completenessIndicatorLayout.dotMinGapPx
  return (dotsRowWidthPx - dotCount * completenessIndicatorLayout.dotSizePx) / (dotCount - 1)
}

export const FULL_COMPLETENESS_PERCENTAGE = 100
const DATA_COMPLETENESS_DECIMAL_PLACES = 2

const truncateToDecimalPlaces = (value: number, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces
  return Math.trunc(value * factor) / factor
}

export const formatDataCompletenessPercentage = (qualityPercentage: number): string => {
  if (qualityPercentage === FULL_COMPLETENESS_PERCENTAGE) return numberFormat(FULL_COMPLETENESS_PERCENTAGE)

  const truncatedPercentage = truncateToDecimalPlaces(qualityPercentage, DATA_COMPLETENESS_DECIMAL_PLACES)
  return numberFormat(truncatedPercentage, DATA_COMPLETENESS_DECIMAL_PLACES)
}

export const getDataCompletenessType = (completenessPercentage: number): DataCompletenessType => {
  if (completenessPercentage === 0) return 'missing'
  if (completenessPercentage === FULL_COMPLETENESS_PERCENTAGE) return 'complete'
  return 'partial'
}

const getSortableCompletenessPercentage = (completenessPercentage: number | null | undefined): number => completenessPercentage ?? -1

export const filterIncompleteDataCompletenessItems = (items: IDataCompletenessItem[]): IDataCompletenessItem[] =>
  items.filter(item => item.completenessPercentage == null || item.completenessPercentage !== FULL_COMPLETENESS_PERCENTAGE)

export const sortDataCompletenessItemsByPercentage = (items: IDataCompletenessItem[]): IDataCompletenessItem[] => {
  const pinnedItems = items.filter(item => item.isPinned)
  const unpinnedItems = items.filter(item => !item.isPinned)
  return [...pinnedItems, ...sortUnpinnedDataCompletenessItemsByPercentage(unpinnedItems)]
}

const sortUnpinnedDataCompletenessItemsByPercentage = (items: IDataCompletenessItem[]): IDataCompletenessItem[] => {
  const itemsWithoutPercentage = items.filter(item => item.completenessPercentage == null)
  const itemsWithPercentage = items.filter(item => item.completenessPercentage != null)

  const incompleteItems = itemsWithPercentage.filter(item => item.completenessPercentage !== FULL_COMPLETENESS_PERCENTAGE)
  const completeItems = itemsWithPercentage.filter(item => item.completenessPercentage === FULL_COMPLETENESS_PERCENTAGE)

  const sortedIncompleteItems = [...incompleteItems].sort(
    (a, b) => getSortableCompletenessPercentage(a.completenessPercentage) - getSortableCompletenessPercentage(b.completenessPercentage)
  )

  return [...sortedIncompleteItems, ...completeItems, ...itemsWithoutPercentage]
}

export const getDataCompletenessQualityColors = (
  qualityPercentage: number | null | undefined,
  hasItems: boolean
): DataQualityColorStyle => {
  return qualityColorMap[getQualityType(qualityPercentage, hasItems)]
}

export const getQualityType = (qualityPercentage: number | null | undefined, hasItems: boolean): DataQualityType => {
  if (!hasItems || qualityPercentage == null) return 'none'
  if (qualityPercentage >= FULL_COMPLETENESS_PERCENTAGE) return 'excellent'
  if (qualityPercentage >= 80) return 'good'
  return 'poor'
}
